import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { throwError } from 'rxjs/internal/observable/throwError';
import { catchError } from 'rxjs/operators';

import ngswConfig from 'ngsw-config.json';
import { EMPTY } from 'rxjs';
import { pages } from 'src/app/shared/config/pages';
import { UserService } from 'src/app/shared/services/user.service';
import { matchesUrlLikeServiceWorker } from 'src/app/shared/utils/cross-functions';
import { urlEndPoints } from '../../shared/config/end-points';
import { AuthenticationService } from './authentication.service';
import { NavigationService } from './navigation.service';
import { StorageService } from './storage.service';
@Injectable({
  providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor {
  constructor(
    private storageService: StorageService,
    private authenticationService: AuthenticationService,
    private navigationService: NavigationService,
    private userService: UserService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    const otpEndpoints = [urlEndPoints.users + 'verify-otp', urlEndPoints.users + 'send-otp'];
    if (req.url === urlEndPoints.login || req.url === urlEndPoints.token || otpEndpoints.includes(req.url)) {
      const step1 = this.addPlatform(req);
      return next.handle(step1);
    } else {
      const token = this.storageService.getAccessToken();
      let selectedHospitalsId = this.userService.getSelectedHospitals();
      selectedHospitalsId = selectedHospitalsId ? selectedHospitalsId : [];
      const principalHospitalId = this.storageService.getData('principalHospitalId');

      const step1 = this.addToken(req, String(token));
      const step2 = this.addHospitals(step1, selectedHospitalsId, principalHospitalId ? principalHospitalId : '');
      const step3 = this.bypassServiceWorkerIfUncachableRequest(step2);
      const step4 = this.addPlatform(step3);

      return next.handle(step4).pipe(
        catchError((err: any) => {
          if (err instanceof HttpErrorResponse) {
            switch ((err as HttpErrorResponse).status) {
              case 400:
                return this.handle400Error(err);
              case 401:
                return this.handle401Error();
              default:
                return throwError(() => err);
            }
          }
          return throwError(() => err);
        })
      );
    }
  }

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
  }

  addPlatform(req: HttpRequest<any>): HttpRequest<any> {
    const setPlatform = req.clone({
      setHeaders: {
        'X-Platform': 'web',
      },
    });
    return setPlatform;
  }

  addHospitals(req: HttpRequest<any>, selectedHospitalsId: string[], principalHospitalId: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        'Selected-Hospitals-Id': selectedHospitalsId,
        'Principal-Hospital-Id': principalHospitalId,
      },
      setParams: {
        'Selected-Hospitals-Id': selectedHospitalsId.join(','),
      },
    });
  }

  bypassServiceWorkerIfUncachableRequest(req: HttpRequest<any>): HttpRequest<any> {
    const cachableRequests = ngswConfig.dataGroups[0].urls.map((url: string) => url.toLowerCase());
    const isCachableRequest =
      req.method === 'GET' &&
      cachableRequests.some((pattern: string) => {
        return matchesUrlLikeServiceWorker(req.urlWithParams.toLowerCase(), pattern);
      });

    if (isCachableRequest) {
      return req;
    } else {
      return req.clone({
        setHeaders: {
          'ngsw-bypass': 'true',
        },
      });
    }
  }

  handle400Error(
    error: HttpErrorResponse
  ): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    if (error && error.status === 400 && error.error && error.error.error === 'invalid_grant') {
      // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
      return this.logoutUser();
    }
    throw new Error(error.message);
  }

  logoutUser() {
    this.authenticationService.logout();
    this.navigationService.navigateByUrl(pages.login).catch();
    return EMPTY;
  }

  handle401Error() {
    if (!this.authenticationService.isAuthenticated()) {
      this.navigationService.navigateByUrl(pages.login).catch();
    }
    return this.logoutUser();
  }
}
